(() => {
  const TAG = 'countdown-announcement';

  class CountdownAnnouncement extends HTMLElement {
    connectedCallback() {
      if (this.initialized) return;
      this.initialized = true;
      this.units = {
        hours: this.querySelector('[data-ca-hours]'),
        minutes: this.querySelector('[data-ca-minutes]'),
        seconds: this.querySelector('[data-ca-seconds]')
      };
      this.status = this.querySelector('[data-ca-status]');
      this.message = this.querySelector('[data-ca-message]');
      this.originalParent = this.parentNode;
      this.originalNextSibling = this.nextSibling;
      this.mount();
      this.bindVariantChanges();
      this.start();
    }

    disconnectedCallback() {
      window.clearInterval(this.timer);
      if (this.variantElement && this.variantHandler) {
        this.variantElement.removeEventListener('change', this.variantHandler);
      }
    }

    mount() {
      const selector = this.dataset.mountSelector?.trim();
      if (!selector) return;
      let target;
      try { target = document.querySelector(selector); } catch (_) { return; }
      if (!target || target === this || this.contains(target)) return;

      const position = this.dataset.mountPosition;
      if (position === 'inside-start') target.prepend(this);
      else if (position === 'inside-end') target.append(this);
      else if (position === 'beforebegin') target.before(this);
      else target.after(this);
    }

    bindVariantChanges() {
      const selector = this.dataset.variantSelector?.trim();
      if (!selector) return;
      try { this.variantElement = document.querySelector(selector); } catch (_) { return; }
      if (!this.variantElement) return;
      this.variantHandler = () => this.loadVariantTarget(this.variantElement.value);
      this.variantElement.addEventListener('change', this.variantHandler);
    }

    loadVariantTarget(variantId) {
      const targets = this.readJson('[data-ca-variant-targets]');
      const target = targets?.[String(variantId)];
      if (!target) return;
      this.dataset.target = target;
      this.start();
    }

    readJson(selector) {
      const node = this.querySelector(selector);
      if (!node) return null;
      try { return JSON.parse(node.textContent); } catch (_) { return null; }
    }

    start() {
      window.clearInterval(this.timer);
      this.hidden = false;
      this.classList.remove('countdown-announcement--complete');
      this.targetTime = this.resolveTarget();
      this.update();
      if (!this.targetTime) return;
      this.timer = window.setInterval(() => this.update(), 1000);
    }

    resolveTarget() {
      const mode = this.dataset.countdownMode || 'fixed';
      const now = new Date();

      if (mode === 'duration') {
        const storageKey = `${this.dataset.storageKey || TAG}:target`;
        const duration = Math.max(1, Number(this.dataset.durationMinutes) || 60) * 60000;
        let target;
        try { target = Number(sessionStorage.getItem(storageKey)); } catch (_) { target = 0; }
        if (!target || target <= Date.now()) {
          target = Date.now() + duration;
          try { sessionStorage.setItem(storageKey, String(target)); } catch (_) {}
        }
        return target;
      }

      if (mode === 'daily') {
        const match = String(this.dataset.dailyTime || '23:59').match(/^(\d{1,2}):(\d{2})$/);
        if (!match) return null;
        const target = new Date(now);
        target.setHours(Math.min(23, Number(match[1])), Math.min(59, Number(match[2])), 0, 0);
        if (target <= now) target.setDate(target.getDate() + 1);
        return target.getTime();
      }

      const parsed = Date.parse(this.dataset.target || '');
      return Number.isFinite(parsed) ? parsed : null;
    }

    update() {
      if (!this.targetTime) {
        this.handleUnavailable();
        return;
      }

      const remaining = Math.max(0, this.targetTime - Date.now());
      const totalSeconds = Math.floor(remaining / 1000);
      const values = {
        hours: Math.floor(totalSeconds / 3600),
        minutes: Math.floor((totalSeconds % 3600) / 60),
        seconds: totalSeconds % 60
      };

      Object.entries(values).forEach(([key, value]) => {
        if (this.units[key]) this.units[key].textContent = String(value).padStart(2, '0');
      });

      if (remaining <= 0) this.complete();
    }

    complete() {
      window.clearInterval(this.timer);
      const behavior = this.dataset.expiryBehavior || 'message';
      if (behavior === 'hide') {
        this.hidden = true;
        return;
      }
      if (behavior === 'restart' && this.dataset.countdownMode === 'duration') {
        try { sessionStorage.removeItem(`${this.dataset.storageKey || TAG}:target`); } catch (_) {}
        this.start();
        return;
      }
      this.classList.add('countdown-announcement--complete');
      if (this.message && this.dataset.expiredText) this.message.textContent = this.dataset.expiredText;
      if (this.status) this.status.textContent = this.dataset.expiredText || '';
    }

    handleUnavailable() {
      window.clearInterval(this.timer);
      if (this.dataset.hideWhenUnavailable === 'true') this.hidden = true;
      else if (this.status) this.status.textContent = this.dataset.unavailableText || '';
    }
  }

  if (!customElements.get(TAG)) customElements.define(TAG, CountdownAnnouncement);
})();
